import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteSEO from "@/components/RouteSEO";
import ImpersonationBanner from "@/components/ImpersonationBanner";

import HomePage from "@/pages/HomePage";
import CoursesPage from "@/pages/CoursesPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import LessonPlayerPage from "@/pages/LessonPlayerPage";
import QuizPage from "@/pages/QuizPage";
import LivePage from "@/pages/LivePage";
import EducationPage from "@/pages/EducationPage";
import InstructorsPage from "@/pages/InstructorsPage";
import PublicationsPage from "@/pages/PublicationsPage";
import DashboardPage from "@/pages/DashboardPage";
import TeachPage from "@/pages/TeachPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CPDPage from "@/pages/CPDPage";
import AdminPage from "@/pages/AdminPage";
import ExamTakerPage from "@/pages/ExamTakerPage";
import ExamReviewPage from "@/pages/ExamReviewPage";
import VerifyPage from "@/pages/VerifyPage";
import OrganisationsPage from "@/pages/OrganisationsPage";
import CohortProgressPage from "@/pages/CohortProgressPage";
import FacultyApplyPage from "@/pages/FacultyApplyPage";
import AccreditationPage from "@/pages/AccreditationPage";
import CPDRenewalPage from "@/pages/CPDRenewalPage";
import { AccountPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordPages";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import AmbassadorsPage from "@/pages/AmbassadorsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AmbassadorApplyPage from "@/pages/AmbassadorApplyPage";
import AmbassadorDashboardPage from "@/pages/AmbassadorDashboardPage";
import AmbassadorDirectoryPage from "@/pages/AmbassadorDirectoryPage";
import AmbassadorLegalPage from "@/pages/AmbassadorLegalPage";
import { ReferralTracker, ReferralRedirect } from "@/lib/referral";
import PoliciesV2Page from "@/pages/PoliciesV2Page";

function AppRouter() {
  return (
    <div className="min-h-screen flex flex-col">
      <RouteSEO />
      <ReferralTracker />
      <a href="#main-content"
         className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-[var(--navy)] focus:text-white focus:px-4 focus:py-2 focus:rounded-full focus:text-sm focus:font-semibold">
        Skip to main content
      </a>
      <Navbar />
      <ImpersonationBanner />
      <main id="main-content" className="flex-1" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/cohort-progress/:token" element={<CohortProgressPage />} />
          <Route path="/join-faculty" element={<FacultyApplyPage />} />
          <Route path="/accreditation" element={<AccreditationPage />} />
          <Route path="/organisations" element={<OrganisationsPage />} />
          <Route path="/ambassadors" element={<AmbassadorsPage />} />
          <Route path="/ambassadors/apply" element={<AmbassadorApplyPage />} />
          <Route path="/ambassadors/directory" element={<AmbassadorDirectoryPage />} />
          <Route path="/ambassadors/legal/:slug" element={<AmbassadorLegalPage />} />
          <Route path="/ambassador" element={<ProtectedRoute><AmbassadorDashboardPage /></ProtectedRoute>} />
          <Route path="/r/:code" element={<ReferralRedirect />} />
          <Route path="*" element={<NotFoundPage />} />
          <Route path="/cpd-renewal" element={<CPDRenewalPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/education" element={<EducationPage />} />
          <Route path="/instructors" element={<InstructorsPage />} />
          <Route path="/policies" element={<PoliciesV2Page />} />
          <Route path="/publications" element={<PublicationsPage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/courses/:id" element={<CourseDetailPage />} />
          <Route path="/live" element={<LivePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />

          <Route path="/dashboard" element={
            <ProtectedRoute><DashboardPage /></ProtectedRoute>
          } />
          <Route path="/cpd" element={
            <ProtectedRoute><CPDPage /></ProtectedRoute>
          } />
          <Route path="/teach" element={
            <ProtectedRoute role="instructor"><TeachPage /></ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute role="admin"><AdminPage /></ProtectedRoute>
          } />
          <Route path="/learn/:id/lesson/:lessonId" element={
            <ProtectedRoute><LessonPlayerPage /></ProtectedRoute>
          } />
          <Route path="/learn/:id/quiz" element={
            <ProtectedRoute><QuizPage /></ProtectedRoute>
          } />
          <Route path="/exams/take/:courseId" element={
            <ProtectedRoute><ExamTakerPage /></ProtectedRoute>
          } />
          <Route path="/admin/exam-attempts/:attemptId" element={
            <ProtectedRoute><ExamReviewPage /></ProtectedRoute>
          } />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

